import { useMemo, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PriceChart } from './PriceChart';
import { openInNewTab } from '../../../../utils/newTab';
import type { TimeRange } from './PriceChart';
import { useContainerWidth } from '../../hooks/useContainerWidth';
import { useAssetClustering } from '../../hooks/useAssetClustering';
import { useIndicatorOverlay } from '../../hooks/useIndicatorOverlay';

interface EmbeddedAssetChartProps {
    ticker: string;
    assetType?: string;
    /** ISO date strings [from, to] — defines the initial zoom viewport. */
    timeRange: [string, string];
    /** Brief LLM-generated context shown above the chart. */
    context?: string;
    /** Chart area height in px, not including the header strip. */
    height?: number;
}

/**
 * Derives the closest TimeRange bucket from a day span.
 * This determines what data useAssetPrices fetches (always "last N days from
 * now"), while the ISO timeRange pair is applied as the initial zoom viewport.
 */
function inferTimeRange(from: Date, to: Date): TimeRange {
    const days = (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24);
    if (days <= 7) return '1W';
    if (days <= 31) return '1M';
    if (days <= 90) return '3M';
    if (days <= 366) return '1Y';
    return 'ALL';
}

export function EmbeddedAssetChart({
    ticker,
    assetType,
    timeRange: timeRangeRaw,
    context,
    height = 360,
}: EmbeddedAssetChartProps) {
    // Measure width here, at the outer wrapper level, rather than relying on
    // PriceChart's internal useContainerWidth in compact mode. In compact mode
    // PriceChart returns its containerRef Box as the root element, meaning the
    // ResizeObserver inside PriceChart fires asynchronously — if data also loads
    // at the same time React batches the updates and the D3 effect runs once with
    // width still 0 → w < 0 → early return → blank chart. Measuring one level up
    // avoids this because the conversation-thread layout is established before
    // this component mounts, so the ResizeObserver fires immediately on attach.
    const { ref: chartWrapperRef, width: containerWidth } = useContainerWidth();

    const [fromDate, toDate] = useMemo(() => {
        return [new Date(timeRangeRaw[0]), new Date(timeRangeRaw[1])];
    }, [timeRangeRaw]);

    const initialTimeRange = useMemo(
        () => inferTimeRange(fromDate, toDate),
        [fromDate, toDate]
    );
    const [activeTimeRange, setActiveTimeRange] =
        useState<TimeRange>(initialTimeRange);

    const [zoomRange, setZoomRange] = useState<[Date, Date] | null>(() => {
        if (
            !isNaN(fromDate.getTime()) &&
            !isNaN(toDate.getTime()) &&
            fromDate < toDate
        ) {
            return [fromDate, toDate];
        }
        return null;
    });

    const href = `/markets/asset/${assetType}/${ticker}`;

    const isCrypto = assetType === 'cryptocurrency';

    // Clustering
    // '__none__' is the sentinel value from the dropdown for "no clustering"
    const [preferredClustering, setPreferredClustering] = useState<
        string | null
    >('__indicator__');
    const {
        overlay: clusteringOverlay,
        availableSeries: clusteringAvailableSeries,
    } = useAssetClustering(
        ticker,
        activeTimeRange,
        preferredClustering === '__none__' ||
            preferredClustering === '__indicator__'
            ? null
            : preferredClustering
    );

    const { overlay: indicatorOverlay } = useIndicatorOverlay(
        ticker,
        activeTimeRange
    );
    console.log('containerWidth: ' + containerWidth);
    return (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '6px',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.875,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Typography
                    sx={{
                        flex: 1,
                        fontSize: '12px',
                        fontWeight: 600,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {ticker} / USD
                </Typography>
                <IconButton
                    size="small"
                    onClick={() => openInNewTab(href)}
                    sx={{ color: 'text.disabled', flexShrink: 0 }}
                    title="Open full view"
                >
                    <OpenInNewIcon sx={{ fontSize: 13 }} />
                </IconButton>
            </Box>
            {/* Chart — ref on this wrapper so width is measured here, not inside
                PriceChart's compact root element (which has a timing gap). */}
            <Box ref={chartWrapperRef}>
                <PriceChart
                    compact={false}
                    chartHeight={height}
                    containerWidth={containerWidth}
                    ticker={ticker}
                    assetType={assetType}
                    timeRange={activeTimeRange}
                    onTimeRangeChange={setActiveTimeRange}
                    zoomRange={zoomRange}
                    onZoomRangeChange={setZoomRange}
                    clusteringOverlay={
                        isCrypto &&
                        preferredClustering !== '__none__' &&
                        preferredClustering !== '__indicator__'
                            ? clusteringOverlay
                            : null
                    }
                    clusteringAvailableSeries={
                        isCrypto ? clusteringAvailableSeries : undefined
                    }
                    clusteringSelectedId={
                        isCrypto ? preferredClustering : undefined
                    }
                    onClusteringChange={
                        isCrypto ? setPreferredClustering : undefined
                    }
                    indicatorOverlay={isCrypto ? indicatorOverlay : null}
                />
            </Box>
        </Box>
    );
}
